
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import GenerateBillForm from "../components/GenerateBillForm";
import BillView from "../components/BillView";
import { API_BASE_URL } from "../../../../config";

const BillTable = ({ search }) => {
  const [visits, setVisits] = useState([]);
  const [filteredVisits, setFilteredVisits] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedVisitForBill, setSelectedVisitForBill] = useState(null);
  const [selectedBillId, setSelectedBillId] = useState(null);

  // ✅ Permissions state
  const [adminPermissions, setAdminPermissions] = useState({
    finalBill: { add: false }
  });
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [reload, setReload] = useState(false);

  // ✅ Fetch admin permissions
  const fetchAdminPermissions = async () => {
    const token = localStorage.getItem("token");
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/get-admin`,
        { headers: { token } }
      );

      if (response.data.success) {
        const adminData = response.data.result.admin;
        setAdminPermissions(adminData.permissions || {});
        setIsSuperAdmin(adminData.isSuperAdmin || false);
      }
    } catch (error) {
      // console.error("Error fetching admin permissions:", error);
      setAdminPermissions({ finalBill: { add: false } });
    }
  };

  // ✅ Permission helpers
  const hasPermission = (action) => {
    return isSuperAdmin || (adminPermissions.finalBill && adminPermissions.finalBill[action]);
  };

  const handleRestrictedAction = (action, callback) => {
    if (!hasPermission(action)) {
      toast.error(`You don't have permission to ${action} bills`);
      return;
    }
    callback();
  };

  // ✅ Safe extractor for API result (customer / project)
  const extractData = (res, mainKey) => {
    if (!res?.data?.success) return {};
    return res.data.result?.[mainKey] || res.data.result || {};
  };

  // ✅ Reusable fetch function (memoized)
  const fetchData = useCallback(async () => {
    setLoading(true);
    const loadingToast = toast.loading("Fetching visits...");
    try {
      const token = localStorage.getItem("token");
      const visitRes = await axios.get(
        `${API_BASE_URL}/api/propertyVisit/get-all-visits`,
        { headers: { token } }
      );

      toast.dismiss(loadingToast);

      if (visitRes.data.success) {
        let visitsData = visitRes.data.result || [];

        // 🔗 Enrich data
        const enrichedVisits = await Promise.all(
          visitsData.map(async (visit) => {
            let customer = {};
            let project = {};
            let bill = null;

            try {
              // ---- Customer ----
              if (visit.customerId) {
                if (typeof visit.customerId === "string") {
                  try {
                    const custRes = await axios.get(
                      `${API_BASE_URL}/api/customer/get-customer/${visit.customerId}`,
                      { headers: { token } }
                    );
                    if (custRes.data.success) {
                      customer = extractData(custRes, "customer");
                    }
                  } catch (err) {
                    // console.error("Customer API error:", err);
                  }
                } else {
                  customer = visit.customerId;
                }
              }

              // ---- Project ----
              if (visit.projectId) {
                if (typeof visit.projectId === "string") {
                  try {
                    const projRes = await axios.get(
                      `${API_BASE_URL}/api/project/get-project/${visit.projectId}`,
                      { headers: { token } }
                    );
                    if (projRes.data.success) {
                      project = extractData(projRes, "project");
                    }
                  } catch (err) {
                    // console.error("Project API error:", err);
                  }
                } else {
                  project = visit.projectId;
                }
              }

              // ---- Bill ----
              if (visit._id) {
                try {
                  const billRes = await axios.get(
                    `${API_BASE_URL}/api/finalbillRecord/get-bill-by-visit/${visit._id}`,
                    { headers: { token } }
                  );
                  if (billRes.data.success) {
                    bill = billRes.data.result?.finalBillRecord || null;
                  }
                } catch (err) {
                  // console.error("Bill fetch error:", err);
                }
              }
            } catch (err) {
              // console.error("Error enriching visit:", err);
            }

            return {
              ...visit,
              customerName: customer.customerName || "N/A",
              customerNumber: customer.customerNumber || "N/A",
              projectName: project.projectName || "N/A",
              projectAddress: project.projectAddress || "N/A",
              projectType: project.propertyType || "N/A",
              ratePerSqft: project.ratePerSqft || "—",
              flatPrice: project.flatPrice || "—",
              bill,
            };
          })
        );

        setVisits(enrichedVisits);
        setFilteredVisits(enrichedVisits);
        toast.success("Visits fetched successfully!");
      } else {
        toast.error("No visits found.");
      }
    } catch (error) {
      // console.error("API Error:", error);
      toast.dismiss(loadingToast);
      toast.error("Failed to fetch visits");
    }
    setLoading(false);
  }, []);

  // ✅ Initial load
  useEffect(() => {
    fetchAdminPermissions();
    fetchData();
  }, [fetchData,reload]);

  // ---- Filter by Search ----
  useEffect(() => {
    if (!search) {
      setFilteredVisits(visits);
    } else {
      const lower = search.toLowerCase();
      const filtered = visits.filter(
        (visit) =>
          visit.customerName?.toLowerCase().includes(lower) ||
          visit.customerNumber?.toLowerCase().includes(lower) ||
          visit.projectName?.toLowerCase().includes(lower) ||
          visit.projectType?.toLowerCase().includes(lower) ||
          visit.projectAddress?.toLowerCase().includes(lower)
      );
      setFilteredVisits(filtered);
    }
  }, [search, visits,reload]);

  const tableHeaders = [
    "customerName",
    "customerNumber",
    "projectName",
    "projectType",
    "projectAddress",
    "ratePerSqft",
    "flatPrice",
    "status",
    "visitDate",
    "createdAt",
    "Bill",
    "Actions",
  ];

  const formatHeader = (field) => {
    return field
      .replace(/([A-Z])/g, " $1")
      .replace(/_/g, " ")
      .trim()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const performDelete = async (billId) => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API_BASE_URL}/api/finalbillRecord/delete-bill/${billId}`,
        {},
        { headers: { token } }
      );

      if (res.data.success) {
        toast.success(res.data.message || "Bill deleted successfully");
        window.location.reload();
      } else {
        toast.error(res.data.message || "Failed to delete bill");
      }
    } catch (error) {
      toast.error("Error deleting bill");
    }
  };


  if (loading) return <p className="p-4 dark:text-white">Loading visits...</p>;

  return (
    <div className="overflow-x-auto rounded-lg shadow-md">
      <table className="dark:text-gray-10 min-w-full border-collapse border border-gray-300 text-left text-sm text-gray-700">
        <thead className="text-black sticky top-0 z-20 border-b border-gray-200 bg-gray-100 text-xs font-bold dark:bg-gray-700 dark:text-white">
          <tr>
            <th className="sticky left-0 top-0 z-30 whitespace-nowrap border border-gray-300 bg-gray-100 px-3 py-2 dark:bg-gray-700">
              SL No
            </th>
            {tableHeaders.map((header) => (
              <th
                key={header}
                className="whitespace-nowrap border border-gray-300 px-4 py-2"
              >
                {formatHeader(header)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filteredVisits.length > 0 ? (
            filteredVisits.map((visit, index) => (
              <tr
                key={visit._id}
                className="hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                <td className="sticky left-0 z-10 whitespace-nowrap border border-gray-300 bg-gray-50 px-3 py-2 dark:bg-navy-700">
                  {index + 1}
                </td>

                {tableHeaders.map((header) => {
                  if (header === "visitDate" && visit.visitDate) {
                    return (
                      <td key={header} className="border border-gray-300 px-4 py-2">
                        {new Date(visit.visitDate).toLocaleString()}
                      </td>
                    );
                  } else if (header === "createdAt" && visit.createdAt) {
                    return (
                      <td key={header} className="border border-gray-300 px-4 py-2">
                        {new Date(visit.createdAt).toLocaleString()}
                      </td>
                    );
                  } else if (header === "status") {
                    return (
                      <td key={header} className="border border-gray-300 px-4 py-2 font-semibold">
                        <span
                          className={`rounded-full px-2 py-1 text-xs font-semibold
                          ${visit.status === "Finalized"
                              ? "bg-green-100 text-green-700"
                              : "bg-gray-100 text-gray-700"
                            }`}
                        >
                          {visit.status}
                        </span>
                      </td>
                    );
                  } else if (header === "Bill") {
                    return (
                      <td key={header} className="min-w-[140px] border border-gray-300 p-3">
                        <span
                          className={`rounded-full px-2 py-1 text-xs font-semibold
                          ${!visit.bill
                              ? "bg-red-100 text-red-700"
                              : "bg-green-100 text-green-700"
                            }`}
                        >
                          {visit.bill ? "Generated" : "Not Generated"}
                        </span>
                      </td>
                    );
                  } else if (header === "Actions") {
                    return (
                      <td key={header} className="min-w-[140px] border border-gray-300 p-3">
                        {!visit.bill ? (
                          <button
                            onClick={() =>
                              handleRestrictedAction("add", () =>
                                setSelectedVisitForBill(visit)
                              )
                            }
                            disabled={!hasPermission("add")}
                            className={`rounded px-2 py-1 text-xs text-white transition-all duration-200 ${hasPermission("add")
                              ? "bg-blue-600 hover:bg-blue-700"
                              : "bg-gray-400 cursor-not-allowed opacity-60"
                              }`}
                            title={
                              !hasPermission("add")
                                ? "You don't have permission to generate bills"
                                : "Generate Bill"
                            }
                          >
                            Generate Bill
                          </button>
                        ) : (
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => setSelectedBillId(visit.bill._id)}
                              className="rounded bg-green-600 px-2 py-1 text-xs text-white hover:bg-green-700"
                            >
                              View Bill
                            </button>

                            <button
                              onClick={() => {
                                performDelete(visit.bill._id);
                              }}
                              disabled={!hasPermission("add")}
                              className={`rounded px-2 py-1 text-xs text-white transition-all duration-200 ${hasPermission("add")
                                ? "bg-blue-600 hover:bg-blue-700"
                                : "bg-gray-400 cursor-not-allowed opacity-60"
                                }`}
                              title={
                                !hasPermission("add")
                                  ? "You don't have permission to delete bills"
                                  : "Delete Bill"
                              }
                            >
                              Delete Bill
                            </button>
                          </div>

                        )}
                      </td>
                    );
                  } else {
                    return (
                      <td key={header} className="border border-gray-300 px-4 py-2">
                        {visit[header] || "—"}
                      </td>
                    );
                  }
                })}
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={tableHeaders.length}
                className="border border-gray-300 px-4 py-2 text-center"
              >
                No visits found
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* ✅ Generate Bill Modal */}
      {selectedVisitForBill && (
        <GenerateBillForm
          visit={selectedVisitForBill}
          onClose={() => setSelectedVisitForBill(null)}
          onBillGenerated={() => {
            fetchData(); // ✅ refresh after success
            setSelectedVisitForBill(null);
          }}
        />
      )}

      {/* ✅ Bill View Modal */}
      {selectedBillId && (
        <BillView
          billId={selectedBillId}
          onClose={() => setSelectedBillId(null)}
          onBillUpdated={fetchData} // ✅ optional refresh
        />
      )}
    </div>
  );
};

export default BillTable;
